/**
 * La bozza di una versione del listino, da rivedere prima di pubblicarla: cosa cambia prezzo, cosa esce dal listino,
 * cosa va verificato col fornitore. Sotto, l'interruttore degli avvisi (spento finché non lo accende chi pubblica) e il
 * bottone «Pubblica», che passa sempre dalla conferma.
 */
import React, { useMemo, useState } from 'react';
import { Box, Button, FormControlLabel, Link, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import type { BozzaDelListino as Bozza, VoceDelConfronto } from '../../services/listiniFornitoriService';
import { BarraDelConfronto } from './BarraDelConfronto';
import { useFormatiListino } from './formati';
import { chiaveDelMotivo, cosaHaTrovato, vociCheCambiano } from './vociDellaBozza';

type Sezione = 'cambiano' | 'escono' | 'da_verificare';
const MONO = { fontFamily: tokens.font.mono, fontSize: 13 } as const;
const NUMERO = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const Codice: React.FC<{ v: VoceDelConfronto }> = ({ v }) => (
  <TableCell>
    <Typography sx={MONO}>{v.codice ?? '—'}</Typography>
    {v.nome && <Typography variant="caption" color="text.secondary">{v.nome}</Typography>}
  </TableCell>
);

const Vuota: React.FC<{ testo: string }> = ({ testo }) => <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{testo}</Typography>;

interface Props {
  bozza: Bozza;
  onPubblica: (avvisa: boolean) => void;
}

export const BozzaDelListino: React.FC<Props> = ({ bozza, onPubblica }) => {
  const { t } = useTranslation('listini');
  const f = useFormatiListino();
  const [sezione, setSezione] = useState<Sezione>('cambiano');
  const [avvisa, setAvvisa] = useState(false);
  const cambiano = useMemo(() => vociCheCambiano(bozza.voci), [bozza]);
  const conteggio = { cambiano: cambiano.length, escono: bozza.voci.spariti.length, da_verificare: bozza.voci.da_verificare.length };
  const pdf = bozza.fonte.file_id ? `https://drive.google.com/file/d/${encodeURIComponent(bozza.fonte.file_id)}/view` : null;

  return (
    <Box sx={{ borderTop: `1px solid ${tokens.color.line}`, pt: 2.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'baseline' }} spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6">{t('bozza.titolo', { numero: bozza.numero })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {bozza.fonte.data_documento
              ? t('bozza.preparata', { quando: f.momento(bozza.creata_il), data: f.giorno(bozza.fonte.data_documento) })
              : t('bozza.preparata_senza_data', { quando: f.momento(bozza.creata_il) })}
          </Typography>
        </Box>
        {pdf && (
          <Link href={pdf} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 40 }}>
            {t('bozza.apri_pdf')} <OpenInNew sx={{ fontSize: 16 }} />
          </Link>
        )}
      </Stack>

      <BarraDelConfronto riepilogo={bozza.riepilogo} />

      <Tabs value={sezione} onChange={(_, v: Sezione) => setSezione(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mt: 2, borderBottom: `1px solid ${tokens.color.line}`, '& .MuiTab-root': { minHeight: 44, textTransform: 'none' } }}>
        {(['cambiano', 'escono', 'da_verificare'] as const).map((s) => (
          <Tab key={s} value={s} label={`${t(`bozza.sezioni.${s}`)} (${conteggio[s]})`} />
        ))}
      </Tabs>

      <Box sx={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
        {sezione === 'cambiano' && (cambiano.length ? (
          <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
            <TableHead><TableRow>
              <TableCell>{t('bozza.colonne.codice')}</TableCell>
              <TableCell align="right">{t('bozza.colonne.prima')}</TableCell>
              <TableCell align="right">{t('bozza.colonne.dopo')}</TableCell>
              <TableCell align="right">{t('bozza.colonne.differenza')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {cambiano.map((v) => (
                <TableRow key={v.id}>
                  <Codice v={v} />
                  <TableCell align="right" sx={{ ...NUMERO, color: 'text.secondary' }}>{f.euro(v.prima)}</TableCell>
                  <TableCell align="right" sx={{ ...NUMERO, fontWeight: 600 }}>{f.euro(v.dopo)}</TableCell>
                  <TableCell align="right" sx={{ ...NUMERO, color: (v.dopo ?? 0) > (v.prima ?? 0) ? tokens.color.warning : tokens.color.success }}>{f.variazione(v.prima, v.dopo)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Vuota testo={t('bozza.vuota.cambiano')} />)}

        {sezione === 'escono' && (bozza.voci.spariti.length ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1.5 }}>{t('bozza.spiega_escono')}</Typography>
            <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
              <TableHead><TableRow>
                <TableCell>{t('bozza.colonne.codice')}</TableCell>
                <TableCell align="right">{t('bozza.colonne.prima')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {bozza.voci.spariti.map((v) => (
                  <TableRow key={v.id}><Codice v={v} /><TableCell align="right" sx={NUMERO}>{f.euro(v.prima)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : <Vuota testo={t('bozza.vuota.escono')} />)}

        {sezione === 'da_verificare' && (bozza.voci.da_verificare.length ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1.5 }}>{t('bozza.spiega_verifica')}</Typography>
            <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
              <TableHead><TableRow>
                <TableCell>{t('bozza.colonne.codice')}</TableCell>
                <TableCell align="right">{t('bozza.colonne.prima')}</TableCell>
                <TableCell>{t('bozza.colonne.motivo')}</TableCell>
                <TableCell>{t('bozza.colonne.nel_listino')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {bozza.voci.da_verificare.map((v) => (
                  <TableRow key={v.id}>
                    <Codice v={v} />
                    <TableCell align="right" sx={NUMERO}>{f.euro(v.prima)}</TableCell>
                    <TableCell>{(() => { const k = chiaveDelMotivo(v); return k ? t(`bozza.motivi.${k}`) : (v.perche ?? ''); })()}</TableCell>
                    <TableCell sx={{ ...MONO, fontSize: 12 }}>{cosaHaTrovato(v, (n) => f.euro(n))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : <Vuota testo={t('bozza.vuota.da_verificare')} />)}
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: tokens.color.lineSoft }}>
        <Box>
          <FormControlLabel control={<Switch checked={avvisa} onChange={(e) => setAvvisa(e.target.checked)} />} label={t('pubblica.avvisa')} sx={{ minHeight: 40 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: { xs: 0, sm: 6 } }}>{t('pubblica.avvisa_spiega')}</Typography>
        </Box>
        <Button variant="contained" size="large" onClick={() => onPubblica(avvisa)} sx={{ minHeight: 44, flexShrink: 0 }}>{t('pubblica.bottone', { numero: bozza.numero })}</Button>
      </Stack>
    </Box>
  );
};
